import type React from "react";
import type { reportingPeriod } from "@duress/shared";
import { fmtHours, fmtPct, fmtInt } from "@/lib/format";
import { DataTable, type DataTableColumn } from "@/components/DataTable";
import { EmptyState } from "@/components/EmptyState";
import { ErrorCard } from "@/components/ErrorCard";
import { LoadingSkeleton } from "@/components/LoadingSkeleton";
import { useDashboardView, type SelectedView } from "@/features/periods/usePeriods";

type UserMetrics = reportingPeriod.UserMetrics;
type RankedUser = UserMetrics & { rank: number; extended_rate: number };

const numCell = (v: unknown): React.ReactNode => (
  <span className="num">{fmtInt(typeof v === "number" ? v : Number(v))}</span>
);

const hoursCell = (v: unknown): React.ReactNode => (
  <span className="num">{fmtHours(typeof v === "number" ? v : Number(v))}</span>
);

const pctCell = (v: unknown): React.ReactNode => (
  <span className="num">{fmtPct(typeof v === "number" ? v : Number(v))}</span>
);

const userCell = (v: unknown): React.ReactNode => (
  <span className="font-head font-medium">{typeof v === "string" ? v : ""}</span>
);

const teamCell = (v: unknown): React.ReactNode => (
  <span className="text-muted-foreground">{typeof v === "string" ? v : ""}</span>
);

const rankCell = (v: unknown): React.ReactNode => (
  <span className="num text-muted-foreground">
    #{fmtInt(typeof v === "number" ? v : Number(v))}
  </span>
);

function rankUsers(users: UserMetrics[]): RankedUser[] {
  return [...users]
    .sort((a, b) => b.sessions - a.sessions)
    .map((u, i) => ({
      ...u,
      rank: i + 1,
      extended_rate: u.sessions > 0 ? (u.extended_sessions / u.sessions) * 100 : 0,
    }));
}

const COLUMNS: DataTableColumn<RankedUser>[] = [
  { key: "rank", label: "Rank", render: rankCell },
  { key: "user", label: "User", render: userCell },
  { key: "store", label: "Team", render: teamCell },
  { key: "sessions", label: "Sessions", render: numCell },
  { key: "total_hours", label: "Total Hours", render: hoursCell },
  { key: "productive_hours", label: "Productive Hours", render: hoursCell },
  { key: "extended_sessions", label: "Extended Sessions", render: numCell },
  { key: "extended_rate", label: "Extended Rate", render: pctCell },
];

// ── Pure presentational component ────────────────────────────────────────────

export interface TopUsersProps {
  data: reportingPeriod.DashboardData;
}

export function TopUsers({ data }: TopUsersProps): React.JSX.Element {
  const users = data.methodology.users;

  if (users.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <p className="text-4xl mb-4">📊</p>
        <p className="text-sm text-muted-foreground max-w-xs">
          No users in this period yet. Upload a reporting period with session data to see this
          view.
        </p>
      </div>
    );
  }

  const rows = rankUsers(users);

  return (
    <div className="space-y-4">
      <div className="finding-blue">
        <p className="font-head font-semibold text-sm mb-1 text-pill-blue">Methodology Note</p>
        <p className="text-sm text-foreground">
          Users ranked by session count, drawn from the period's processed sessions. Users with
          blank names are excluded. Extended Rate is the share of a user's sessions that ran past
          24 hours.
        </p>
      </div>

      <div className="card-duress p-5">
        <DataTable<RankedUser>
          columns={COLUMNS}
          data={rows}
          defaultSort={{ key: "sessions", dir: "desc" }}
          searchKeys={["user", "store"]}
        />
      </div>
    </div>
  );
}

// ── Smart wrapper that fetches data ───────────────────────────────────────────

export function TopUsersTab({
  clientId,
  view,
}: {
  clientId: string;
  view: SelectedView | null;
}): React.JSX.Element {
  const { data, isLoading, error, refetch } = useDashboardView(clientId, view);

  if (!view) {
    return <EmptyState body="Select a reporting period above." />;
  }

  if (isLoading) {
    return <LoadingSkeleton variant="table" />;
  }

  if (error || !data) {
    return <ErrorCard message="Failed to load period data." onRetry={refetch} />;
  }

  return <TopUsers data={data} />;
}
